import React, { useState } from 'react';
import { Typography, Tag, Space, Modal, Drawer, Button, Card, Timeline, Row, Col, Grid } from 'antd';
import { FileTextOutlined, ClockCircleOutlined, UserOutlined, HistoryOutlined, MessageOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Paragraph, Title } = Typography;

/**
 * PA Comments Display Component
 * Displays PA comments from facility, showing latest comment prominently
 * and full comment history if multiple comments exist
 * 
 * Supports both old field names (batch_comment, pa_batch_comment) and
 * new structure (pa_comment, pa_comments array)
 */
const PACommentsDisplay = ({ 
  claim, 
  response, 
  encounter,
  showFullHistory = true,
  compact = false,
  /** `trigger` — icon/button only; opens comment history in modal/drawer */
  variant = compact ? 'compact' : 'default',
  className,
  /** When set, shown instead of rendering nothing when there are no comments */
  emptyMessage = null,
}) => {
  // Hooks MUST be called at the top level, before any conditional logic or early returns
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false);

  /** Ant Design breakpoints — updates on resize (not one-shot window.innerWidth). */
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = !!(screens.md && !screens.lg);

  // Extract comments from various possible field names (backward compatibility)
  const getLatestComment = () => {
    // Try new structure first
    if (claim?.pa_comment) return claim.pa_comment;
    if (response?.pa_comment) return response.pa_comment;
    if (encounter?.pa_comment) return encounter.pa_comment;
    
    // Try old field names
    if (claim?.batch_comment) return claim.batch_comment;
    if (response?.batch_comment) return response.batch_comment;
    if (response?.pa_batch_comment) return response.pa_batch_comment;
    if (response?.pa_request_comment) return response.pa_request_comment;
    if (encounter?.batch_comment) return encounter.batch_comment;
    
    return null;
  };

  // Get all comments array (new structure)
  const getAllComments = () => {
    // Try new structure first
    if (claim?.pa_comments && Array.isArray(claim.pa_comments) && claim.pa_comments.length > 0) {
      return claim.pa_comments;
    }
    if (response?.pa_comments && Array.isArray(response.pa_comments) && response.pa_comments.length > 0) {
      return response.pa_comments;
    }
    if (encounter?.pa_comments && Array.isArray(encounter.pa_comments) && encounter.pa_comments.length > 0) {
      return encounter.pa_comments;
    }
    
    return null;
  };

  const latestComment = getLatestComment();
  const allComments = getAllComments();
  const commentHistory = (allComments && allComments.length > 0)
    ? allComments
    : (latestComment ? [{
      id: 'latest',
      comment: latestComment,
      submitted_at: response?.updated_at || encounter?.updated_at || claim?.updated_at || response?.checkin_date || encounter?.checkin_date,
    }] : []);
  const latestCommentMeta = commentHistory[0] || null;
  const hasHistory = showFullHistory && commentHistory.length > 0;
  const hasMultipleComments = commentHistory.length > 1;

  // If no comments at all, optionally show emptyMessage; otherwise render nothing
  if (!latestComment && commentHistory.length === 0) {
    if (emptyMessage) {
      return (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {emptyMessage}
        </Text>
      );
    }
    return null;
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return dayjs(dateString).format('MMM D, YYYY [at] h:mm A');
    } catch (e) {
      return dateString;
    }
  };

  // Get commenter name
  const getCommenterName = (comment) => {
    if (comment.first_name || comment.last_name) {
      return `${comment.first_name || ''} ${comment.last_name || ''}`.trim();
    }
    return null;
  };

  const openHistory = () => {
    // Use drawer on mobile, modal on desktop
    if (isMobile) {
      setHistoryDrawerVisible(true);
    } else {
      setHistoryModalVisible(true);
    }
  };

  // Render comment history (used in modal/drawer)
  const renderCommentHistory = () => {
    if (!commentHistory.length) {
      return (
        <div style={{ textAlign: 'center', padding: isMobile ? '30px 16px' : '40px 20px' }}>
          <MessageOutlined style={{ fontSize: isMobile ? '40px' : '48px', color: '#d9d9d9', marginBottom: '16px' }} />
          <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>No comment history available</Text>
        </div>
      );
    }

    return (
      <div
        style={{
          padding: isMobile ? '4px 0' : '8px 0',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        <Timeline
          mode="left"
          items={commentHistory.map((comment, index) => ({
            color: index === 0 ? '#1890ff' : '#d9d9d9',
            children: (
              <Card
                size="small"
                style={{
                  marginBottom: isMobile ? '12px' : '16px',
                  border: index === 0 ? '2px solid #1890ff' : '1px solid #e8e8e8',
                  boxShadow: index === 0 ? '0 2px 8px rgba(24, 144, 255, 0.15)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
                  background: index === 0 ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' : '#fafafa',
                  maxWidth: '100%',
                  minWidth: 0,
                }}
                bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
              >
                <div style={{ marginBottom: isMobile ? '10px' : '12px', minWidth: 0 }}>
                  <Space size="small" wrap style={{ width: '100%', rowGap: 8 }}>
                    {index === 0 && (
                      <Tag color="blue" icon={<FileTextOutlined />} style={{ 
                        fontSize: isMobile ? '11px' : '12px',
                        marginBottom: '4px'
                      }}>
                        Latest
                      </Tag>
                    )}
                    <Tag icon={<ClockCircleOutlined />} color="default" style={{ 
                      fontSize: isMobile ? '11px' : '12px',
                      marginBottom: '4px'
                    }}>
                      {formatDate(comment.submitted_at)}
                    </Tag>
                    {getCommenterName(comment) && (
                      <Tag icon={<UserOutlined />} color="default" style={{ 
                        fontSize: isMobile ? '11px' : '12px',
                        marginBottom: '4px'
                      }}>
                        {getCommenterName(comment)}
                      </Tag>
                    )}
                    <Tag color="default" style={{ 
                      fontSize: isMobile ? '11px' : '12px',
                      marginBottom: '4px'
                    }}>
                      #{comment.id || (commentHistory.length - index)}
                    </Tag>
                  </Space>
                </div>
                <Paragraph style={{ 
                  margin: 0,
                  fontSize: isMobile ? '15px' : '14px',
                  lineHeight: '1.9',
                  color: '#333',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'anywhere',
                  maxWidth: '100%',
                  minWidth: 0,
                  letterSpacing: '0.01em',
                  fontWeight: 400
                }}>
                  {comment.comment || latestComment}
                </Paragraph>
              </Card>
            )
          }))}
        />
      </div>
    );
  };

  if (variant === 'trigger') {
    return (
      <>
        <Button
          type="default"
          size="small"
          icon={<MessageOutlined />}
          className={className}
          onClick={(e) => {
            e.stopPropagation();
            openHistory();
          }}
        >
          PA{hasMultipleComments ? ` (${commentHistory.length})` : ''}
        </Button>

        <Modal
          title={
            <Space wrap>
              <MessageOutlined style={{ color: '#1890ff', fontSize: isMobile ? '18px' : '20px' }} />
              <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600 }}>PA Comments</span>
              <Tag color="blue" style={{ fontSize: isMobile ? '11px' : '12px' }}>{commentHistory.length}</Tag>
            </Space>
          }
          open={historyModalVisible}
          onCancel={() => setHistoryModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setHistoryModalVisible(false)} size={isMobile ? 'large' : 'middle'}>
              Close
            </Button>
          ]}
          width={isMobile ? '95%' : isTablet ? '85%' : 720}
          style={{ top: isMobile ? 10 : 20 }}
          bodyStyle={{
            maxHeight: isMobile ? '75vh' : '70vh',
            overflowY: 'auto',
            padding: isMobile ? '16px' : '20px',
          }}
        >
          {renderCommentHistory()}
        </Modal>

        <Drawer
          title={
            <Space wrap>
              <MessageOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: 600 }}>PA Comments</span>
              <Tag color="blue" style={{ fontSize: '11px' }}>{commentHistory.length}</Tag>
            </Space>
          }
          open={historyDrawerVisible}
          onClose={() => setHistoryDrawerVisible(false)}
          width="100%"
          placement="bottom"
          height="90%"
          bodyStyle={{ padding: '16px' }}
        >
          {renderCommentHistory()}
        </Drawer>
      </>
    );
  }

  if (compact || variant === 'compact') {
    // Compact view for claim cards
    return (
      <>
        <div style={{
          marginTop: '8px',
          padding: isMobile ? '12px' : '14px 16px',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          border: '1px solid #91d5ff',
          borderRadius: '8px',
          fontSize: isMobile ? '13px' : '12px',
          lineHeight: '1.7',
          boxShadow: '0 2px 4px rgba(24, 144, 255, 0.1)',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: isMobile ? 'stretch' : 'center', 
            justifyContent: 'space-between',
            marginBottom: '8px',
            flexWrap: 'wrap',
            gap: '8px',
            flexDirection: isMobile ? 'column' : 'row',
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              fontWeight: 600,
              color: '#1890ff',
              fontSize: isMobile ? '13px' : '12px'
            }}>
              <FileTextOutlined style={{ fontSize: isMobile ? '16px' : '14px' }} />
              <span>PA Comment</span>
            </div>
            {hasHistory && (
              <Button
                type="link"
                size="small"
                icon={<HistoryOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  openHistory();
                }}
                style={{ 
                  padding: isMobile ? '4px 8px' : '0 4px',
                  height: 'auto',
                  fontSize: isMobile ? '12px' : '11px',
                  color: '#1890ff',
                  whiteSpace: 'nowrap',
                  alignSelf: isMobile ? 'flex-start' : 'auto',
                }}
              >
                {isMobile ? (hasMultipleComments ? `All (${commentHistory.length})` : 'History') : (hasMultipleComments ? `View All (${commentHistory.length})` : 'View History')}
              </Button>
            )}
          </div>
          <div style={{ 
            color: '#333',
            fontSize: isMobile ? '14px' : '12px',
            lineHeight: '1.8',
            wordWrap: 'break-word',
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
            letterSpacing: '0.01em',
            maxWidth: '100%',
            minWidth: 0,
          }}>
            {latestComment}
          </div>
          {latestCommentMeta?.submitted_at && (
            <div style={{ 
              marginTop: '8px',
              fontSize: isMobile ? '11px' : '10px',
              color: '#8c8c8c',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              flexWrap: 'wrap'
            }}>
              <ClockCircleOutlined />
              <span>{formatDate(latestCommentMeta.submitted_at)}</span>
            </div>
          )}
        </div>

        {/* History Modal */}
        <Modal
          title={
            <Space wrap>
              <MessageOutlined style={{ color: '#1890ff', fontSize: isMobile ? '18px' : '20px' }} />
              <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600 }}>PA Comment History</span>
              <Tag color="blue" style={{ fontSize: isMobile ? '11px' : '12px' }}>{commentHistory.length} comments</Tag>
            </Space>
          }
          open={historyModalVisible}
          onCancel={() => setHistoryModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setHistoryModalVisible(false)} size={isMobile ? 'large' : 'middle'}>
              Close
            </Button>
          ]}
          width={isMobile ? '95%' : isTablet ? '85%' : 800}
          style={{ top: isMobile ? 10 : 20 }}
          bodyStyle={{ 
            maxHeight: isMobile ? '75vh' : '70vh', 
            overflowY: 'auto', 
            padding: isMobile ? '16px' : '24px' 
          }}
        >
          {renderCommentHistory()}
        </Modal>

        {/* History Drawer (Mobile) */}
        <Drawer
          title={
            <Space wrap>
              <MessageOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: 600 }}>PA Comment History</span>
              <Tag color="blue" style={{ fontSize: '11px' }}>{commentHistory.length} comments</Tag>
            </Space>
          }
          open={historyDrawerVisible}
          onClose={() => setHistoryDrawerVisible(false)}
          width="100%"
          placement="bottom"
          height="90%"
          bodyStyle={{ padding: '16px' }}
        >
          {renderCommentHistory()}
        </Drawer>
      </>
    );
  }


  // Full view for detail modals
  return (
    <>
      <Card
        style={{
          marginBottom: isMobile ? '16px' : '20px',
          border: '1px solid #91d5ff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(24, 144, 255, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
          maxWidth: '100%',
          minWidth: 0,
        }}
        bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
      >
        <Row gutter={[16, 16]} align={isMobile ? 'top' : 'middle'}>
          <Col xs={24} sm={24} md={16} style={{ minWidth: 0 }}>
            <Space wrap align="start" style={{ width: '100%', rowGap: 8 }}>
              <div style={{
                width: isMobile ? '36px' : '40px',
                height: isMobile ? '36px' : '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                flexShrink: 0
              }}>
                <FileTextOutlined style={{ fontSize: isMobile ? '18px' : '20px', color: '#fff' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
                <Title level={isMobile ? 5 : 4} style={{ margin: 0, color: '#1890ff', fontSize: isMobile ? '16px' : '18px' }}>
                  Facility PA Comment
                </Title>
                {hasHistory && (
                  <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px', display: 'block', marginTop: '4px' }}>
                    {commentHistory.length} total comment{commentHistory.length > 1 ? 's' : ''}
                  </Text>
                )}
              </div>
            </Space>
          </Col>
          {hasHistory && (
            <Col xs={24} sm={24} md={8} style={{ textAlign: isMobile ? 'left' : 'right', minWidth: 0 }}>
              <Button
                type="primary"
                icon={<HistoryOutlined />}
                onClick={openHistory}
                size={isMobile ? 'middle' : 'default'}
                block={isMobile}
                style={{
                  borderRadius: '6px',
                  boxShadow: '0 2px 4px rgba(24, 144, 255, 0.3)',
                  whiteSpace: 'normal',
                  height: 'auto',
                  lineHeight: 1.35,
                }}
              >
                {isMobile ? (hasMultipleComments ? 'View All' : 'View History') : (hasMultipleComments ? 'View All Comments' : 'View Comment History')}
              </Button>
            </Col>
          )}
        </Row>

        {/* Latest Comment - Prominently Displayed */}
        {latestComment && (
          <Card
            size="small"
            style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              border: '2px solid #1890ff',
              borderRadius: '8px',
              marginTop: '16px',
              marginBottom: hasHistory ? '16px' : '0',
              boxShadow: '0 2px 8px rgba(24, 144, 255, 0.15)'
            }}
            bodyStyle={{ padding: isMobile ? '14px' : '16px' }}
          >
            <div style={{ marginBottom: isMobile ? '10px' : '12px', minWidth: 0 }}>
              <Space size="small" wrap style={{ width: '100%', rowGap: 8 }}>
                <Tag color="blue" icon={<FileTextOutlined />} style={{ 
                  fontSize: isMobile ? '11px' : '12px', 
                  padding: isMobile ? '3px 6px' : '4px 8px',
                  marginBottom: '4px'
                }}>
                  Latest Comment
                </Tag>
                {latestCommentMeta?.submitted_at && (
                  <Tag icon={<ClockCircleOutlined />} color="default" style={{ 
                    fontSize: isMobile ? '11px' : '12px',
                    marginBottom: '4px'
                  }}>
                    {formatDate(latestCommentMeta.submitted_at)}
                  </Tag>
                )}
                {latestCommentMeta && getCommenterName(latestCommentMeta) && (
                  <Tag icon={<UserOutlined />} color="default" style={{ 
                    fontSize: isMobile ? '11px' : '12px',
                    marginBottom: '4px'
                  }}>
                    {getCommenterName(latestCommentMeta)}
                  </Tag>
                )}
              </Space>
            </div>
            <Paragraph style={{ 
              margin: 0,
              fontSize: isMobile ? '15px' : '14px',
              lineHeight: '1.9',
              color: '#333',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              overflowWrap: 'anywhere',
              maxWidth: '100%',
              minWidth: 0,
              letterSpacing: '0.01em',
              fontWeight: 400
            }}>
              {latestComment}
            </Paragraph>
          </Card>
        )}
      </Card>

      {/* History Modal */}
      <Modal
        title={
          <Space wrap>
            <div style={{
              width: isMobile ? '28px' : '32px',
              height: isMobile ? '28px' : '32px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <MessageOutlined style={{ fontSize: isMobile ? '16px' : '18px', color: '#fff' }} />
            </div>
            <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600 }}>PA Comment History</span>
            <Tag color="blue" style={{ fontSize: isMobile ? '11px' : '12px' }}>{commentHistory.length} comments</Tag>
          </Space>
        }
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryModalVisible(false)} size={isMobile ? 'large' : 'middle'}>
            Close
          </Button>
        ]}
        width={isMobile ? '95%' : isTablet ? '85%' : 800}
        style={{ top: isMobile ? 10 : 20 }}
        bodyStyle={{ 
          maxHeight: isMobile ? '75vh' : '70vh', 
          overflowY: 'auto', 
          padding: isMobile ? '16px' : '24px' 
        }}
      >
        {renderCommentHistory()}
      </Modal>

      {/* History Drawer (Mobile) */}
      <Drawer
        title={
          <Space wrap>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <MessageOutlined style={{ fontSize: '16px', color: '#fff' }} />
            </div>
            <span style={{ fontSize: '16px', fontWeight: 600 }}>PA Comment History</span>
            <Tag color="blue" style={{ fontSize: '11px' }}>{commentHistory.length} comments</Tag>
          </Space>
        }
        open={historyDrawerVisible}
        onClose={() => setHistoryDrawerVisible(false)}
        width="100%"
        placement="bottom"
        height="90%"
        bodyStyle={{ padding: '16px' }}
      >
        {renderCommentHistory()}
      </Drawer>
    </>
  );
};

export default PACommentsDisplay;

